const formatTitle = (value = '') =>
  String(value)
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const HIDDEN_PERMISSION_MODULES = new Set([
  'uploads',
  'imported_catalog',
  'imported_order_reports',
  'exports_order_reports',
  'exports_sales_reports',
  'exports_catalog',
  'reports',
]);

const LEGACY_IMPORT_VIEW = [
  'uploads.view',
  'imported_catalog.view',
  'imported_order_reports.view',
];

const LEGACY_IMPORT_DOWNLOAD = [
  'uploads.view',
  'imported_catalog.export',
  'imported_order_reports.export',
];

const LEGACY_EXPORT_VIEW = [
  'exports_order_reports.view',
  'exports_sales_reports.view',
  'exports_catalog.view',
];

const LEGACY_EXPORT_DOWNLOAD = [
  'exports_order_reports.export',
  'exports_sales_reports.export',
  'exports_catalog.export',
];

export const togglePermissionGroupSelection = (
  selected = [],
  groupPermissions = [],
  allGroupSelected
) => {
  if (allGroupSelected) {
    const toRemove = new Set(groupPermissions);
    return selected.filter((permission) => !toRemove.has(permission));
  }

  return [...new Set([...selected, ...groupPermissions])];
};

export const normalizeRolePermissionsForDisplay = (permissions = []) => {
  const set = new Set(permissions);

  if (LEGACY_IMPORT_VIEW.some((permission) => set.has(permission))) {
    set.add('imported_data.view');
  }
  if (set.has('uploads.create')) {
    set.add('imported_data.import');
  }
  if (LEGACY_IMPORT_DOWNLOAD.some((permission) => set.has(permission))) {
    set.add('imported_data.download');
  }
  if (set.has('uploads.delete')) {
    set.add('imported_data.delete');
  }
  if (LEGACY_EXPORT_VIEW.some((permission) => set.has(permission))) {
    set.add('exports.view');
  }
  if (LEGACY_EXPORT_DOWNLOAD.some((permission) => set.has(permission))) {
    set.add('exports.download');
  }
  if (set.has('uploads.delete')) {
    set.add('exports.delete');
  }

  [...set].forEach((permission) => {
    const [moduleName] = permission.split('.');
    if (HIDDEN_PERMISSION_MODULES.has(moduleName)) {
      set.delete(permission);
    }
  });

  return [...set];
};

export const filterVisiblePermissionGroups = (groups = []) =>
  groups.filter(
    (group) => !HIDDEN_PERMISSION_MODULES.has(group.module || group.key)
  );

const groupFlatPermissions = (permissions = []) => {
  const grouped = {};

  permissions.forEach((permission) => {
    if (!permission?.name) return;

    const [moduleName, actionName] = permission.name.split('.');

    if (!moduleName) return;

    if (!grouped[moduleName]) {
      grouped[moduleName] = {
        key: moduleName,
        module: moduleName,
        title: formatTitle(moduleName),
        permissions: [],
      };
    }

    grouped[moduleName].permissions.push({
      id: permission.id,
      name: permission.name,
      action: actionName,
      label: formatTitle(actionName || permission.name),
    });
  });

  return Object.values(grouped);
};

/**
 * Accepts either flat permissions [{ id, name }] or grouped API response
 * [{ module, title, permissions: [...] }] from GET /roles/permissions/grouped
 */
export const normalizePermissionGroups = (data = []) => {
  if (!Array.isArray(data) || data.length === 0) return [];

  const isGrouped = data.some((item) => Array.isArray(item?.permissions));

  if (isGrouped) {
    return data.map((group) => {
      const key = group.key || group.module || 'unknown';
      return {
        ...group,
        key,
        module: group.module || key,
        title: group.title || formatTitle(key),
        permissions: (group.permissions || [])
          .filter((p) => p?.name)
          .map((p) => ({
            ...p,
            action: p.action || p.name.split('.').slice(1).join('.'),
            label: p.label || formatTitle(p.action || p.name.split('.').pop()),
          })),
      };
    });
  }

  return groupFlatPermissions(data);
};

export const preparePermissionGroupsForUI = (data = []) =>
  filterVisiblePermissionGroups(normalizePermissionGroups(data));

export const formatPermissionDisplayLabel = (
  permissionName = '',
  getModuleLabel = null,
  getActionLabel = null
) => {
  const [moduleName, ...actionParts] = String(permissionName).split('.');
  if (!moduleName) return formatTitle(permissionName);

  const action = actionParts.join('.');
  const moduleLabel = getModuleLabel
    ? getModuleLabel(moduleName)
    : formatTitle(moduleName);
  const actionLabel = getActionLabel
    ? getActionLabel(action || permissionName)
    : formatTitle(action || permissionName);

  return `${moduleLabel} - ${actionLabel}`;
};

const getModuleFromPermission = (permissionName = '') => {
  const [module] = String(permissionName).split('.');
  return module || null;
};

/**
 * When selecting any non-view permission, auto-include module.view.
 * When manually deselecting view, remove all permissions for that module.
 * Deselecting other permissions does not auto-remove view.
 */
export const applyPermissionSelectionRules = (
  selected = [],
  permissionName,
  isSelecting
) => {
  const module = getModuleFromPermission(permissionName);
  if (!module) return selected;

  const viewKey = `${module}.view`;
  const action = permissionName.slice(module.length + 1);

  if (isSelecting) {
    if (action === 'view') {
      return [...new Set([...selected, permissionName])];
    }

    return [...new Set([...selected, permissionName, viewKey])];
  }

  if (action === 'view') {
    return selected.filter((p) => !p.startsWith(`${module}.`));
  }

  return selected.filter((p) => p !== permissionName);
};
